import {
  isTaskTypeViewSourcePlacement,
  type TaskTypeViewPlacement,
  type TaskTypeViewRegion,
} from '@erp/shared-types';
import { Card } from 'src/components/ui/card';
import { Label } from 'src/components/ui/label';
import { SupplierDocumentLineMatch } from './SupplierDocumentLineMatch';
import { TaskTypeViewField } from './TaskTypeViewField';
import { TaskTypeViewLineList } from './TaskTypeViewLineList';
import { TaskTypeViewMatch } from './TaskTypeViewMatch';
import { TaskTypeViewSource } from './TaskTypeViewSource';
import type { CorrectionCommitResult } from './useCommitTaskCorrection';
import {
  findSchemaField,
  placementLabel,
  type TaskViewSchemaField,
} from './taskTypeViewValue';
import { groupPlacements, type ScalarPlacement } from './groupPlacements';

type Commit = (
  next: Record<string, unknown>,
) => void | Promise<CorrectionCommitResult | void>;

/** Regions stacked in one column. The side panel renders aside regions itself. */
export function TaskTypeViewRenderer({
  regions,
  schemaFields,
  input,
  workingCopy,
  anchorType,
  anchorId,
  onCommit,
}: {
  regions: readonly TaskTypeViewRegion[];
  schemaFields: readonly TaskViewSchemaField[];
  input: Record<string, unknown>;
  workingCopy: Record<string, unknown>;
  anchorType: string | null;
  anchorId: string | null;
  onCommit: Commit;
}) {
  return (
    <div className="flex flex-col gap-2" data-testid="task-type-view">
      {regions.map((region) => (
        <TaskTypeViewRegionCard
          key={region.id}
          region={region}
          schemaFields={schemaFields}
          input={input}
          workingCopy={workingCopy}
          anchorType={anchorType}
          anchorId={anchorId}
          onCommit={onCommit}
        />
      ))}
    </div>
  );
}

export function TaskTypeViewRegionCard({
  region,
  hideTitle = false,
  schemaFields,
  input,
  workingCopy,
  anchorType,
  anchorId,
  onCommit,
}: {
  region: TaskTypeViewRegion;
  /** The side panel names the region on its tab, so the card skips the heading. */
  hideTitle?: boolean;
  schemaFields: readonly TaskViewSchemaField[];
  input: Record<string, unknown>;
  workingCopy: Record<string, unknown>;
  anchorType: string | null;
  anchorId: string | null;
  onCommit: Commit;
}) {
  const groups = groupPlacements(region.placements);
  return (
    <Card className="gap-2 p-3">
      {region.title && !hideTitle ? (
        <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-stone-500">
          {region.title}
        </div>
      ) : null}
      <div className="flex flex-col gap-3">
        {groups.map((group) =>
          group.kind === 'scalars' ? (
            <ScalarPlacementGrid
              key={group.placements[0]?.id}
              placements={group.placements}
              schemaFields={schemaFields}
              input={input}
              workingCopy={workingCopy}
              onCommit={onCommit}
            />
          ) : (
            <TaskTypeViewPlacementView
              key={group.placement.id}
              placement={group.placement}
              schemaFields={schemaFields}
              input={input}
              workingCopy={workingCopy}
              anchorType={anchorType}
              anchorId={anchorId}
              onCommit={onCommit}
            />
          ),
        )}
      </div>
    </Card>
  );
}

function ScalarPlacementGrid({
  placements,
  schemaFields,
  input,
  workingCopy,
  onCommit,
}: {
  placements: readonly ScalarPlacement[];
  schemaFields: readonly TaskViewSchemaField[];
  input: Record<string, unknown>;
  workingCopy: Record<string, unknown>;
  onCommit: Commit;
}) {
  return (
    <div
      className="grid grid-cols-[minmax(7rem,10rem)_minmax(0,1fr)] items-start gap-x-3 gap-y-2"
      data-testid="task-view-scalar-grid"
    >
      {placements.map((placement) => {
        const label = placementLabel(
          placement.config.label,
          findSchemaField(schemaFields, placement.config.path),
          placement.config.path,
        );
        return (
          <ScalarPlacementRow
            key={placement.id}
            label={label}
            placement={placement}
            schemaFields={schemaFields}
            input={input}
            workingCopy={workingCopy}
            onCommit={onCommit}
          />
        );
      })}
    </div>
  );
}

function ScalarPlacementRow({
  label,
  placement,
  schemaFields,
  input,
  workingCopy,
  onCommit,
}: {
  label: string;
  placement: ScalarPlacement;
  schemaFields: readonly TaskViewSchemaField[];
  input: Record<string, unknown>;
  workingCopy: Record<string, unknown>;
  onCommit: Commit;
}) {
  const control =
    placement.component === 'match' ? (
      <TaskTypeViewMatch
        placement={placement}
        schemaFields={schemaFields}
        input={input}
        workingCopy={workingCopy}
        onCommit={onCommit}
        hideLabel
        ariaLabel={label}
      />
    ) : (
      <TaskTypeViewField
        placement={placement}
        schemaFields={schemaFields}
        input={input}
        workingCopy={workingCopy}
        onCommit={onCommit}
        hideLabel
        ariaLabel={label}
      />
    );
  return (
    <>
      <Label className="pt-2 text-xs font-medium text-muted-foreground">
        {label}
      </Label>
      <div className="min-w-0 max-w-md">{control}</div>
    </>
  );
}

function TaskTypeViewPlacementView({
  placement,
  schemaFields,
  input,
  workingCopy,
  anchorType,
  anchorId,
  onCommit,
}: {
  placement: TaskTypeViewPlacement;
  schemaFields: readonly TaskViewSchemaField[];
  input: Record<string, unknown>;
  workingCopy: Record<string, unknown>;
  anchorType: string | null;
  anchorId: string | null;
  onCommit: Commit;
}) {
  if (isTaskTypeViewSourcePlacement(placement)) {
    return (
      <TaskTypeViewSource
        kind={placement.component}
        placementId={placement.id}
        config={placement.config}
        anchorType={anchorType}
        anchorId={anchorId}
        workingCopy={workingCopy}
      />
    );
  }
  if (placement.component === 'supplierLineMatch') {
    return (
      <SupplierDocumentLineMatch
        placement={placement}
        input={input}
        workingCopy={workingCopy}
        onCommit={onCommit}
      />
    );
  }
  if (placement.component === 'lineList') {
    return (
      <TaskTypeViewLineList
        placement={placement}
        schemaFields={schemaFields}
        input={input}
        workingCopy={workingCopy}
        onCommit={onCommit}
      />
    );
  }
  if (placement.component === 'match') {
    return (
      <TaskTypeViewMatch
        placement={placement}
        schemaFields={schemaFields}
        input={input}
        workingCopy={workingCopy}
        onCommit={onCommit}
      />
    );
  }
  return (
    <TaskTypeViewField
      placement={placement}
      schemaFields={schemaFields}
      input={input}
      workingCopy={workingCopy}
      onCommit={onCommit}
    />
  );
}
